import {labelAccessibleName, RepositoryLabelToken} from '@github-ui/label-token/RepositoryLabelToken'
import {XIcon} from '@primer/octicons-react'
import {IconButton, Link, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useAnalytics} from '@github-ui/use-analytics'
import {useLabelStyles} from '@github-ui/use-label-styles'
import {useCallback, useLayoutEffect, useMemo, useRef, type CSSProperties, type ReactNode, type RefObject} from 'react'

import styles from './LabelsList.module.css'

/**
 * This interface is the same as that of @github-ui/item-picker/label's Label
 * but it doesn't seem appropriate to couple this list to the picker
 * in that way
 */
export interface Label {
  readonly id: string
  readonly url: string
  readonly name: string
  readonly nameHTML: string
  readonly color: string
  readonly description: string | null | undefined
  /**
   * Set when the label has been archived. Optional so producers that don't yet
   * select the field (or predate label archiving) stay assignable.
   */
  readonly archivedAt?: string | null
}

export type LabelsListProps = {
  labels: Label[]
  testId?: string
  /** We display this string when the list of labels is empty, defaults to 'No labels' */
  emptyText?: ReactNode
  onRemoveLabel?: (label: Label) => void
  removingLabelIds?: ReadonlySet<string>
  removeLabelFocusFallbackRef?: RefObject<HTMLButtonElement | null>
}

/**
 *
 * A list of label tokens sorted by name.
 * This component is used in the Issues and Pull Requests sidebars.
 * If we find either one needs to diverge too much we should consider
 * making two separate components instead of trying to make this one fit both.
 *
 */
export function LabelsList({
  labels,
  testId,
  emptyText = 'No labels',
  onRemoveLabel,
  removingLabelIds,
  removeLabelFocusFallbackRef,
}: LabelsListProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const removeButtonElementsRef = useRef(new Map<string, HTMLButtonElement>())
  const focusAfterRemovalRef = useRef<{nextId?: string; previousId?: string; removedId: string} | null>(null)
  const repoIssuesPath = useMemo(() => {
    if (labels.length === 0 || !labels[0]) return ''
    return `${labels[0].url.slice(0, labels[0].url.lastIndexOf('/labels/'))}/issues`
  }, [labels])

  const labelIndexUrl = useCallback(
    (label: Label) => `${repoIssuesPath}?q=${encodeURIComponent(`state:open label:"${label.name}"`)}`,
    [repoIssuesPath],
  )
  const sortedLabels = useMemo(
    () => [...labels].sort((a, b) => (a.name === b.name ? 0 : a.name > b.name ? 1 : -1)),
    [labels],
  )

  useLayoutEffect(() => {
    const focusAfterRemoval = focusAfterRemovalRef.current
    if (!focusAfterRemoval || labels.some(label => label.id === focusAfterRemoval.removedId)) return

    focusAfterRemovalRef.current = null
    if (document.activeElement !== document.body) return

    removeButtonElementsRef.current.get(focusAfterRemoval.nextId ?? '')?.focus()
    if (document.activeElement === document.body) {
      removeButtonElementsRef.current.get(focusAfterRemoval.previousId ?? '')?.focus()
    }
    if (document.activeElement === document.body) removeLabelFocusFallbackRef?.current?.focus()
  }, [labels, removeLabelFocusFallbackRef])

  const onRemove = useCallback(
    (label: Label, restoreFocus: boolean) => {
      const labelIndex = sortedLabels.findIndex(item => item.id === label.id)
      focusAfterRemovalRef.current = restoreFocus
        ? {
            removedId: label.id,
            nextId: sortedLabels[labelIndex + 1]?.id,
            previousId: sortedLabels[labelIndex - 1]?.id,
          }
        : null
      onRemoveLabel?.(label)
    },
    [onRemoveLabel, sortedLabels],
  )

  return (
    <div tabIndex={-1} data-testid={testId} className={styles.labelsListContainer}>
      {labels.length > 0
        ? sortedLabels.map(label =>
            onRemoveLabel ? (
              <RemovableLabel
                key={label.id}
                label={label}
                labelIndexUrl={labelIndexUrl(label)}
                onLinkClick={() => sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABEL_CLICK')}
                onLinkContextMenu={() => sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABEL_RIGHT_CLICK')}
                onRemove={restoreFocus => onRemove(label, restoreFocus)}
                removing={removingLabelIds?.has(label.id) ?? false}
                removeButtonRef={button => {
                  if (button) removeButtonElementsRef.current.set(label.id, button)
                  else removeButtonElementsRef.current.delete(label.id)
                }}
              />
            ) : (
              <LabelLink
                key={label.id}
                label={label}
                labelIndexUrl={labelIndexUrl(label)}
                onLinkClick={() => sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABEL_CLICK')}
                onLinkContextMenu={() => sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABEL_RIGHT_CLICK')}
              />
            ),
          )
        : emptyText}
    </div>
  )
}

function LabelLink({
  label,
  labelIndexUrl,
  onLinkClick,
  onLinkContextMenu,
}: {
  label: Label
  labelIndexUrl: string
  onLinkClick: () => void
  onLinkContextMenu: () => void
}) {
  return (
    <Tooltip
      text={label.description ?? ''}
      type="description"
      aria-label={label.description ?? ''}
      className={clsx(styles.labelDescriptionTooltip, label.description ? styles.tooltipVisible : styles.tooltipHidden)}
    >
      <Link
        href={labelIndexUrl}
        muted
        aria-describedby={`${label.id}-tooltip`}
        className={styles.labelLink}
        onClick={onLinkClick}
        onContextMenu={onLinkContextMenu}
      >
        <RepositoryLabelToken
          label={label}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: 'pointer',
            maxWidth: '100%',
          }}
        />
        <span className="sr-only" id={`${label.id}-tooltip`}>
          {label.description ?? ''}
        </span>
      </Link>
    </Tooltip>
  )
}

function RemovableLabel({
  label,
  labelIndexUrl,
  onLinkClick,
  onLinkContextMenu,
  onRemove,
  removing,
  removeButtonRef,
}: {
  label: Label
  labelIndexUrl: string
  onLinkClick: () => void
  onLinkContextMenu: () => void
  onRemove: (restoreFocus: boolean) => void
  removing: boolean
  removeButtonRef: (button: HTMLButtonElement | null) => void
}) {
  const labelStyles = useLabelStyles(1, `#${label.color}`)
  const labelColorVariables = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(labelStyles).filter(([property]) => property.startsWith('--')),
      ) as CSSProperties,
    [labelStyles],
  )

  return (
    <span className={styles.removableLabelContainer} style={labelColorVariables}>
      <LabelLink
        label={label}
        labelIndexUrl={labelIndexUrl}
        onLinkClick={onLinkClick}
        onLinkContextMenu={onLinkContextMenu}
      />
      <IconButton
        ref={removeButtonRef}
        className={styles.removeLabelButton}
        icon={RemoveLabelIcon}
        variant="invisible"
        style={{
          color: 'color-mix(in srgb, rgb(var(--label-r), var(--label-g), var(--label-b)) 50%, var(--fgColor-default))',
        }}
        aria-label={`Remove label: ${labelAccessibleName(label)}`}
        disabled={removing}
        onClick={event => onRemove(event.detail === 0)}
      />
    </span>
  )
}

function RemoveLabelIcon() {
  return <XIcon size={12} />
}
